<script lang="ts">
  // Consumer
  import HomePage from "./consumer/HomePage.svelte";
  import HeroVideoSection from "./consumer/HeroVideoSection.svelte";
  import PartnerLogosSection from "./consumer/PartnerLogosSection.svelte";
  import HowItWorksSection from "./consumer/HowItWorksSection.svelte";
  import ValuePropositionSection from "./consumer/ValuePropositionSection.svelte";
  import NowFlyingSection from "./consumer/NowFlyingSection.svelte";
  import BentoSection from "./consumer/BentoSection.svelte";
  import FaqSection from "./consumer/FaqSection.svelte";
  import CheckAvailabilityPage from "./consumer/CheckAvailabilityPage.svelte";

  // Business
  import BlogList from "./views/BlogList.svelte";
  import ButtonLink from "./atoms/ButtonLink.svelte";
  import CardSlider from "./views/CardSlider.svelte";
  import Column from "./together/layout/Column.svelte";
  import Columns from "./together/layout/Columns.svelte";
  import Hero from "./views/Hero.svelte";
  import HeroNewsroom from "./views/HeroNewsroom.svelte";
  import LogoGrid from "./views/LogoGrid.svelte";
  import MapOverview from "./views/MapOverview.svelte";
  import Meta from "./components/Meta.svelte";
  import NewsroomList from "./views/NewsroomList.svelte";
  import Page from "./Page.svelte";
  import PostCard from "./molecules/PostCard.svelte";
  import RichText from "./molecules/RichText.svelte";
  import Section from "./together/layout/StoryblokSection.svelte";
  import SideBySide from "./views/SideBySide.svelte";
  import StatsList from "./views/StatsList.svelte";
  import TextAndLink from "./atoms/TextAndLink.svelte";
  import TextCard from "./elements/TextCard.svelte";
  import TextImageSlide from "./molecules/TextImageSlide.svelte";
  import VideoV2 from "./molecules/VideoV2.svelte";

  // Other
  import MaintenancePage from "./MaintenancePage.svelte";

  // Import types
  import type { SvelteComponent } from "svelte";
  import type { SbBlokData } from "@storyblok/svelte";

  // Primary components are used on main pages. They are always loaded and built
  // into the app bundle.
  const primaryComponents: Record<
    string,
    typeof SvelteComponent<{ blok: SbBlokData }>
  > = {
    // Consumer
    homePage: HomePage,
    heroVideoSection: HeroVideoSection,
    partnerLogosSection: PartnerLogosSection,
    howItWorksSection: HowItWorksSection,
    valuePropositionSection: ValuePropositionSection,
    nowFlyingSection: NowFlyingSection,
    bentoSection: BentoSection,
    faqSection: FaqSection,
    checkAvailabilityPage: CheckAvailabilityPage,

    // Business
    blogList: BlogList,
    buttonLink: ButtonLink,
    cardSlider: CardSlider,
    column: Column,
    columns: Columns,
    hero: Hero,
    heroNewsroom: HeroNewsroom,
    logoGrid: LogoGrid,
    mapOverview: MapOverview,
    meta: Meta,
    newsroomList: NewsroomList,
    page: Page,
    postCard: PostCard,
    richText: RichText,
    section: Section,
    sideBySide: SideBySide,
    statsList: StatsList,
    textAndLink: TextAndLink,
    textCard: TextCard,
    textImageSlide: TextImageSlide,
    video: VideoV2,

    // Other
    maintenancePage: MaintenancePage,
  } as const;

  // Secondary components are dynamically loaded as needed. This may slow
  // loading slightly, but it makes the initial app bundle significantly
  // smaller.
  const secondaryComponents: Record<
    string,
    () => Promise<{
      default: typeof SvelteComponent<{ blok: SbBlokData | SbBlokData[] }>;
    }>
  > = {
    // Business
    anchorHash: () => import("./atoms/AnchorHash.svelte"),
    animatedScrollList: () => import("./components/AnimatedScrollList.svelte"),
    appStoreButtons: () => import("./molecules/AppStoreButtons.svelte"),
    audioPlayer: () => import("./components/AudioPlayer.svelte"),
    autonomyStack: () => import("./views/AutonomyStack.svelte"),
    blogCardGrid: () => import("./molecules/FluidCardGrid.svelte"),
    blogFilterDropdown: () => import("./atoms/BlogFilterDropdown.svelte"),
    box: () => import("./elements/Box.svelte"),
    buttonModal: () => import("./atoms/ButtonModal.svelte"),
    cardModalGrid: () => import("./views/CardModalGrid.svelte"),
    careersIntro: () => import("./views/CareersIntro.svelte"),
    careersListing: () => import("./views/CareersListing.svelte"),
    careersTestimonials: () => import("./views/CareersTestimonials.svelte"),
    careersValues: () => import("./views/CareersValues.svelte"),
    category: () => import("./views/Category.svelte"),
    collage: () => import("./views/ImageCollage.svelte"),
    deliveryForm: () => import("./views/DeliveryForm.svelte"),
    deliveryReport: () => import("./views/DeliveryReport.svelte"),
    droneScroller: () => import("./views/DroneScroller.svelte"),
    emailInputWithButton: () =>
      import("./molecules/EmailInputWithButton.svelte"),
    faqs: () => import("./molecules/Faqs.svelte"),
    featuredBlogCard: () => import("./molecules/FeaturedBlogCard.svelte"),
    form: () => import("./molecules/Form.svelte"),
    formBusinessEmailInput: () =>
      import("./molecules/FormBusinessEmailInput.svelte"),
    formSection: () => import("./views/FormSection.svelte"),
    formSelect: () => import("./molecules/FormSelect.svelte"),
    fourColumnGrid: () => import("./views/FourColumnGrid.svelte"),
    fullscreenGallery: () => import("./views/FullscreenGallery.svelte"),
    fullscreenImage: () => import("./views/FullscreenImage.svelte"),
    galleryArticle: () =>
      import("./molecules/galleryItems/GalleryArticle.svelte"),
    galleryImage: () => import("./molecules/galleryItems/GalleryImage.svelte"),
    galleryQuote: () => import("./molecules/galleryItems/GalleryQuote.svelte"),
    galleryVideo: () => import("./molecules/galleryItems/GalleryVideo.svelte"),
    guideCard: () => import("./molecules/GuideCard.svelte"),
    guideCardGrid: () => import("./molecules/FluidCardGrid.svelte"),
    headingContentListItems: () =>
      import("./molecules/HeadingContentListItems.svelte"),
    headingContentListItemsIcons: () =>
      import("./molecules/HeadingContentListItemsIcons.svelte"),
    heroBox: () => import("./elements/HeroBox.svelte"),
    heroCareers: () => import("./views/HeroCareers.svelte"),
    heroContent: () => import("./views/HeroContent.svelte"),
    eventHero: () => import("./event/EventHero.svelte"),
    eventFooter: () => import("./event/EventFooter.svelte"),
    heroQT: () => import("./views/HeroQT.svelte"),
    heroResources: () => import("./views/HeroResources.svelte"),
    heroTwoColumns: () => import("./views/HeroTwoColumns.svelte"),
    heroVideo: () => import("./views/HeroVideo.svelte"),
    highlights: () => import("./views/Highlights.svelte"),
    howItWorks: () => import("./views/HowItWorks.svelte"),
    iframePage: () => import("./views/IframePage.svelte"),
    // @ts-expect-error Image blok prop type is non-standard
    image: () => import("./atoms/Image.svelte"),
    interactiveMap: () => import("./views/InteractiveMapV2.svelte"),
    labelledText: () => import("./atoms/LabelledText.svelte"),
    logoButton: () => import("./molecules/LogoButton.svelte"),
    marqueeImage: () => import("./views/MarqueeImage.svelte"),
    mediaTimerSlider: () => import("./molecules/MediaTimerSlider.svelte"),
    modal: () => import("./molecules/Modal.svelte"),
    modalForm: () => import("./molecules/ModalForm.svelte"),
    multipleSideBySides: () => import("./views/MultipleSideBySides.svelte"),
    newsletterForm: () => import("./views/NewsletterForm.svelte"),
    newsroomLanding: () => import("./views/NewsroomLanding.svelte"),
    newsroomListMultiple: () => import("./views/NewsroomListMultiple.svelte"),
    newsroomSubnav: () => import("./views/NewsroomSubnav.svelte"),
    // @ts-expect-error Overlay blok prop type is non-standard
    overlay: () => import("./molecules/Overlay.svelte"),
    pdfDownload: () => import("./molecules/PdfDownload.svelte"),
    platformOverview: () => import("./views/PlatformOverview.svelte"),
    post: () => import("./views/Post.svelte"),
    postBanner: () => import("./molecules/newsroom/post/PostBanner.svelte"),
    postCallout: () => import("./molecules/newsroom/post/PostCallout.svelte"),
    postDataCallout: () =>
      import("./molecules/newsroom/post/PostDataCallout.svelte"),
    postDataCalloutItem: () =>
      import("./molecules/newsroom/post/PostDataCalloutItem.svelte"),
    postExpandableList: () =>
      import("./molecules/newsroom/post/PostExpandableList.svelte"),
    postList: () => import("./molecules/newsroom/post/PostList.svelte"),
    postListItem: () => import("./molecules/newsroom/post/PostListItem.svelte"),
    postMedia: () => import("./molecules/newsroom/post/PostMedia.svelte"),
    postPullQuote: () =>
      import("./molecules/newsroom/post/PostPullQuote.svelte"),
    postS6: () => import("./views/PostS6.svelte"),
    postText: () => import("./molecules/newsroom/post/PostText.svelte"),
    postTwoCol: () =>
      import("./molecules/newsroom/post/sections/PostTwoCols.svelte"),
    quote: () => import("./molecules/Quote.svelte"),
    scrollspyNav: () => import("./views/ScrollspyNav.svelte"),
    sideBySideWithTimer: () => import("./views/SideBySide.svelte"),
    singleTestimonial: () => import("./views/SingleTestimonial.svelte"),
    table: () => import("./molecules/Table.svelte"),
    testimonialSlider: () => import("./views/TestimonialSlider.svelte"),
    textAccordion: () => import("./molecules/TextAccordion.svelte"),
    textBlock: () => import("./views/TextBlock.svelte"),
    textList: () => import("./molecules/TextList.svelte"),
    threeColumnList: () => import("./views/ThreeColumnList.svelte"),
    threeColumnWithImages: () => import("./views/ThreeColumnWithImages.svelte"),
    tiltedGallery: () => import("./molecules/TiltedGallery.svelte"),
    tiltedGalleryItem: () => import("./molecules/TiltedGalleryItem.svelte"),
    twoColumnCaseStudiesTestimonial: () =>
      import("./views/TwoColumnCaseStudies.svelte"),
    valueProp: () => import("./views/ValueProp.svelte"),
    videoCard: () => import("./molecules/VideoCard.svelte"),
    videoCardGrid: () => import("./molecules/FluidCardGrid.svelte"),
    videoSection: () => import("./views/VideoSection.svelte"),
    videoVimeo: () => import("./molecules/VideoVimeo.svelte"),
    videoYoutube: () => import("./molecules/VideoYoutube.svelte"),
    webinarCard: () => import("./molecules/WebinarCard.svelte"),
    webinarCardGrid: () => import("./molecules/FluidCardGrid.svelte"),
    webinarRegistrationCard: () =>
      import("./molecules/WebinarRegistrationCard.svelte"),
  } as const;

  // TODO: need a union type that gives us all valid Bloks on the site
  export let blok;

  const Component = primaryComponents[blok.component];
  const ComponentLoader = secondaryComponents[blok.component];

  $: {
    if (!Component && !ComponentLoader) {
      console.error(`Tried to load unknown component ${blok.component}`);
    }
  }
</script>

{#if Component}
  <Component {blok} {...$$restProps} />
{:else if ComponentLoader}
  {#await ComponentLoader() then Component}
    <Component.default {blok} {...$$restProps} />
  {/await}
{/if}
